/**
 * buildStamp.js — the identity of a built report, so a stale one is never on offer.
 *
 * A built PDF belongs to the inputs it was built from. Change any of them and the
 * preview, the page count and the Download button must go, or the panel keeps
 * offering a finished-looking document of something else — the "looks finished,
 * isn't" failure the whole Builder exists to remove.
 *
 * ── Why this is a module and not four lines in the component ────────────────
 *
 * Because the thing that keeps going wrong is an input someone forgot to list,
 * and that cannot be tested inside a `useMemo`. Two have already shipped:
 *
 *   · `m.role` and `m.stage` were missing, so clearing a role override took a
 *     draft from 8 pages back to 6 while every mesh id stayed identical — and
 *     left the pre-correction PDF on offer.
 *   · `edits` were missing, so changing a caption kept the previous document.
 *
 * As a pure function the guarantee is assertable: buildStamp.test.js walks every
 * field and requires each one to move the stamp. A new input added to the
 * Builder and not to STAMPED_INPUTS fails that test.
 */

/**
 * Every input that changes what the document says or looks like.
 *
 * Order is irrelevant to correctness — the stamp is only ever compared for
 * equality — but it is kept stable so a diff of two stamps is readable in a bug
 * report.
 */
export const STAMPED_INPUTS = [
  'template',
  'picks',            // which mesh resolves an ambiguous slot
  'parts',            // operator part-type declarations
  'meta',             // names, case id, report version, report DATE, page numbers
  'edits',            // captions and annotations
  'renderOpts',       // the look panel: matcap, relief, colours
  'ctInfo',           // the orbital CT block
  'waiver',           // the orbital quality waiver
  'oldScanOverride',  // three-state: undefined derives, true/false override
  'volumes',          // the measured figures the legend prints
  'volumeState',      // see the note in buildStamp()
  'library',          // reduced to id/role/stage below
  'basket',           // page mode: which pages, in what order, with which meshes
]

/**
 * A stable string identifying this set of inputs.
 *
 * @param {object} inputs  keyed by STAMPED_INPUTS; `library` is the mesh list
 * @returns {string}
 */
export function buildStamp(inputs = {}) {
  const {
    template, picks, parts, meta, edits, renderOpts,
    ctInfo, waiver, oldScanOverride, volumes, volumeState, library, basket,
  } = inputs
  return JSON.stringify([
    template ?? null,
    picks ?? null,
    parts ?? null,
    meta ?? null,
    edits ?? null,
    renderOpts ?? null,
    ctInfo ?? null,
    waiver ?? null,
    // `undefined` is a real value here — "derive from the dates" — and JSON
    // drops it, which would make it indistinguishable from `false`. Mapped to a
    // marker so all three states are stamped distinctly.
    oldScanOverride === undefined ? 'derive' : oldScanOverride,
    volumes ?? null,
    // Volume analysis is ASYNC. Without the STATE in the stamp, a document built
    // while it was still running would carry no legend, and the stamp would not
    // change when the figures landed — so that document would stay on offer as
    // the current one. The values alone are not enough: going from {} (idle) to
    // {} (error) is a real change the operator must see.
    volumeState ?? null,
    // Only the fields that change the document. A `handle` is a File object and
    // would not serialise; a name is already inside the id.
    //
    // 🔴 `side` is here because it is the THIRD instance of this module's own
    // failure mode, caught before it shipped rather than after. The fibula
    // report captions five of its pages from a mesh's side — "Left fibula was
    // used in the reconstruction" — so correcting a wrong side changes what the
    // document SAYS ABOUT THE PATIENT while every mesh id, role and stage stays
    // identical. Without it the pre-correction PDF stays on offer, looking
    // finished, which is exactly what the two shipped cases above did.
    (library ?? []).map((m) => [m.id, m.role, m.stage, m.side ?? null]),
    // Page mode: reordering, removing or adding a page, or choosing a page's
    // mesh, changes the document as surely as a caption does. Without this a
    // reorder would leave the previous PDF on offer, looking finished. `seq` is
    // left out on purpose — it changes nothing printed on its own.
    basket ? [basket.template, basket.entries] : null,
  ])
}
